import axiosInstance from "service/axiosInstance";
import {
  CrmValue,
  CrmValueCategory,
  CrmValueDeleteResult,
  CrmValuePayload,
  CrmValueStatus,
  CrmValueUsage,
  CRM_VALUE_CATEGORY_PATHS,
} from "types/crm/values";

/**
 * Every path is scoped to a category, so this is the one place that knows how
 * a category spells itself in a URL.
 */
const categoryPath = (category: CrmValueCategory) =>
  `/crm/values/${CRM_VALUE_CATEGORY_PATHS[category]}`;

/**
 * Keyed by category first, so one `category(…)` invalidation refreshes both the
 * settings list and the dropdowns that feed off it — renaming a value in
 * Settings has to reach the Contact form too.
 */
export const crmValueQueryKeys = {
  all: ["crm-values"] as const,
  category: (category: CrmValueCategory) =>
    [...crmValueQueryKeys.all, category] as const,
  list: (category: CrmValueCategory) =>
    [...crmValueQueryKeys.category(category), "list"] as const,
  assignable: (category: CrmValueCategory) =>
    [...crmValueQueryKeys.category(category), "assignable"] as const,
  usage: (category: CrmValueCategory, valueUuid: string) =>
    [...crmValueQueryKeys.category(category), "usage", valueUuid] as const,
};

/**
 * Always sends `description`, even empty.
 *
 * The edit is a full replace, so omitting a cleared description would leave it
 * ambiguous between "no description" and "leave the old one alone" — and the
 * spec does not say which the server would pick. An empty string says it
 * plainly.
 */
const toBody = ({ name, description }: CrmValuePayload) => ({
  name: name.trim(),
  description: description?.trim() ?? '',
});

export const crmValueApi = {
  /**
   * GET /crm/values/{category}
   *
   * Not paginated — the lists are small — and already ordered by `sortOrder`,
   * so the response is used as it arrives. The `status` filter is deliberately
   * not sent: the screen shows Active and Inactive together, and the API
   * answers an unknown query enum with a 500 rather than a 400.
   */
  getValues: async (category: CrmValueCategory): Promise<CrmValue[]> => {
    const { data } = await axiosInstance.get(categoryPath(category));
    return data.data ?? [];
  },

  /**
   * GET /crm/values/{category}/assignable
   *
   * What a Contact or Deal dropdown may offer: Active values only, already
   * ordered. Open to any CRM user, unlike the rest of this file — reading the
   * options is not managing them.
   *
   * `usageCount` comes back as 0 here whatever the truth is; the endpoint does
   * not count, so nothing should read it off this list.
   */
  getAssignableValues: async (
    category: CrmValueCategory,
  ): Promise<CrmValue[]> => {
    const { data } = await axiosInstance.get(
      `${categoryPath(category)}/assignable`,
    );
    return data.data ?? [];
  },

  createValue: async (
    category: CrmValueCategory,
    payload: CrmValuePayload,
  ): Promise<CrmValue> => {
    const { data } = await axiosInstance.post(
      categoryPath(category),
      toBody(payload),
    );
    return data.data;
  },

  /** Updates the same row: `uuid` and `key` survive, so records follow the rename. */
  updateValue: async (
    category: CrmValueCategory,
    valueUuid: string,
    payload: CrmValuePayload,
  ): Promise<CrmValue> => {
    const { data } = await axiosInstance.put(
      `${categoryPath(category)}/${valueUuid}`,
      toBody(payload),
    );
    return data.data;
  },

  /**
   * Activate / Deactivate. No record is ever changed by this; it only decides
   * whether the value can be newly selected.
   */
  updateValueStatus: async (
    category: CrmValueCategory,
    valueUuid: string,
    status: CrmValueStatus,
  ): Promise<CrmValue> => {
    const { data } = await axiosInstance.patch(
      `${categoryPath(category)}/${valueUuid}/status`,
      { status },
    );
    return data.data;
  },

  /**
   * GET /crm/values/{category}/{valueUuid}/usage
   *
   * The Delete modal's own read: the count, whether the delete is allowed at
   * all, and the values the records could move to. Read fresh when the modal
   * opens rather than taken from the list row, which may be minutes old.
   */
  getUsage: async (
    category: CrmValueCategory,
    valueUuid: string,
  ): Promise<CrmValueUsage> => {
    const { data } = await axiosInstance.get(
      `${categoryPath(category)}/${valueUuid}/usage`,
    );
    return {
      ...data.data,
      replacementOptions: data.data?.replacementOptions ?? [],
    };
  },

  /**
   * DELETE /crm/values/{category}/{valueUuid}
   *
   * An unused value goes with no body. A value in use needs `replaceWithUuid`:
   * the server moves every record to it and deletes in one transaction, so a
   * failure leaves both the value and its records as they were.
   */
  deleteValue: async (
    category: CrmValueCategory,
    valueUuid: string,
    replaceWithUuid?: string,
  ): Promise<CrmValueDeleteResult> => {
    const { data } = await axiosInstance.delete(
      `${categoryPath(category)}/${valueUuid}`,
      replaceWithUuid ? { data: { replaceWithUuid } } : undefined,
    );
    return data.data;
  },
};
